import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { bookingsAPI } from '../../api/bookings';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { CheckCircle2, Download, Eye, Home, Calendar } from 'lucide-react';
import { getSportColor } from '../../lib/utils';
import DateTimeDisplay from '../../components/booking/DateTimeDisplay';
import DurationPill from '../../components/booking/DurationPill';

export default function BookingConfirmed() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    loadBooking();
  }, [bookingId]);

  const loadBooking = async () => {
    try {
      const data = await bookingsAPI.getBooking(bookingId);
      setBooking(data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDownloadCalendar = async () => {
    try {
      const blob = await bookingsAPI.downloadCalendar(bookingId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `booking-${bookingId}.ics`;
      a.click();
      toast.success('Calendar file downloaded');
    } catch (error) {
      toast.error('Failed to download calendar');
    }
  };

  return (
    <div className="mobile-container min-h-screen flex items-center justify-center p-6">
      <Card className="p-8 text-center bba-card bba-card-accent max-w-md w-full" data-testid="booking-confirmed">
        <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-12 h-12 text-success" />
        </div>
        <h1 className="text-3xl font-black font-heading uppercase mb-3">Booking Confirmed!</h1>
        <p className="text-muted-foreground mb-6">
          Your payment was successful. See you on the court!
        </p>
        {booking && (
          <div className="mb-8 p-4 bg-muted/30 rounded-2xl space-y-3">
            <div className="flex items-center justify-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <DateTimeDisplay
                date={booking.booking_date}
                timeSlot={booking.time_slot}
                duration={booking.duration || 1}
                testId="booking-confirmed"
              />
            </div>
            {booking.duration && booking.duration > 1 && (
              <div className="flex justify-center">
                <DurationPill
                  duration={booking.duration}
                  timeSlot={booking.time_slot}
                  variant="prominent"
                  testId="booking-confirmed"
                />
              </div>
            )}
            {booking.courts_assigned && booking.courts_assigned.length > 0 && (
              <div className="flex justify-center gap-2 mt-2">
                {booking.courts_assigned.map((court) => (
                  <span
                    key={`${court.court_number}-${court.sport}`}
                    className={`sport-badge ${getSportColor(court.sport)}`}
                  >
                    Court {court.court_number}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* No Refund Policy - Informational */}
        <div className="bg-muted/30 border border-border p-4 rounded-xl mt-6">
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            <strong className="text-foreground">No Refund Policy:</strong> All sales are final. We do not offer refunds, exchanges, or cancellations once payment has been processed.
          </p>
        </div>

        <div className="space-y-3">
          <Button
            onClick={handleDownloadCalendar}
            data-testid="download-calendar-button"
            className="w-full btn-primary h-14"
          >
            <Download className="w-5 h-5 mr-2" />
            Add to Calendar
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/booking/${bookingId}`)}
            data-testid="view-booking-button"
            className="w-full h-14 rounded-full"
          >
            <Eye className="w-5 h-5 mr-2" />
            View Booking
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate('/home')}
            data-testid="back-home-button"
            className="w-full"
          >
            <Home className="w-5 h-5 mr-2" />
            Back to Home
          </Button>
        </div>
      </Card>
    </div>
  );
}